import { pickT } from "@repo/shared";
import type { PdfSectionScope } from "@repo/shared/types";
import { useMemo, useRef } from "react";
import { HexColorPicker } from "react-colorful";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useUserLanguage } from "@/lib/i18n/context";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import {
  buildFieldMap,
  flowFieldDisplayRef,
  transformToDisplay,
  transformToStorage,
} from "@/lib/services/placeholder-transformer";
import { AdvisorNoteMenuItems } from "./advisor-note-menu-items";
import { T } from "./text-form.i18n";
import { type ComponentFormProps, NO_ADVISOR_NOTE_PAGES } from "./types";

export type TextConfig = {
  content: string;
  align?: "left" | "center" | "right";
  fontSize?: number;
  maxWidth?: number; // Max width in cm
  color?: string; // Hex color (e.g. "#000000"); undefined = theme default
};

type TextFormProps = ComponentFormProps<TextConfig> & {
  sectionScope?: PdfSectionScope;
};

export function TextForm({
  config,
  onChange,
  flowFields,
  // QL-13 — `{{Quiz.<name>}}` renders the client's result for that quiz.
  quizzes: activeQuizzes = [],
  advisorNotePages = NO_ADVISOR_NOTE_PAGES,
  clientCustomFields = [],
  sectionScope,
}: TextFormProps) {
  const { userLanguage } = useUserLanguage();
  const localize = useLocalize();
  const t = useT(T);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Build field map for placeholder transformation
  const fieldMap = useMemo(
    () =>
      buildFieldMap(clientCustomFields, flowFields, userLanguage, activeQuizzes, advisorNotePages),
    [clientCustomFields, flowFields, userLanguage, activeQuizzes, advisorNotePages],
  );

  // Transform content from storage (IDs) to display (localized names) for the textarea
  const displayContent = useMemo(
    () => transformToDisplay(config.content || "", fieldMap),
    [config.content, fieldMap],
  );

  // Handle content change: transform from display to storage format
  const handleContentChange = (newDisplayContent: string) => {
    const storageContent = transformToStorage(newDisplayContent, fieldMap);
    onChange({ ...config, content: storageContent });
  };

  // Insert a field placeholder
  // displayValue is what appears in the textarea (e.g., "Client.Company Size")
  // It will be transformed to ID format (e.g., "Client.clx123") on save
  const insertField = (displayValue: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentDisplayContent = displayContent;

    // Insert the display value directly (will be converted to ID on save)
    const newDisplayContent = `${currentDisplayContent.substring(0, start)}{{${displayValue}}}${currentDisplayContent.substring(end)}`;

    // Transform to storage format and update
    handleContentChange(newDisplayContent);

    // Set cursor position after inserted text
    requestAnimationFrame(() => {
      textarea.focus();
      const newPos = start + displayValue.length + 4; // +4 for {{ and }}
      textarea.setSelectionRange(newPos, newPos);
    });
  };

  // Client fields (always available)
  const clientFields = [
    { label: t.name, value: "Client.Name" },
    { label: t.type, value: "Client.Type" },
    { label: t.email, value: "Client.Email" },
    { label: t.mobilePhone, value: "Client.MobilePhone" },
    { label: t.ssn, value: "Client.SSN" },
    { label: t.orgId, value: "Client.OrgId" },
    { label: t.country, value: "Client.Country" },
    { label: t.groupMembers, value: "Client.Members" },
    { label: t.representative, value: "Client.Representative" },
  ];

  // Owner fields (advisor / client owner data)
  const ownerFields = [
    { label: t.name, value: "Owner.Name" },
    { label: t.email, value: "Owner.Email" },
    { label: t.phoneNumber, value: "Owner.Phone" },
    { label: t.title, value: "Owner.Title" },
    { label: t.signature, value: "Owner.Signature" },
  ];

  // Flow fields (flow instance data)
  const flowInstanceFields = [
    { label: t.name, value: "Flow.Name" },
    { label: t.cancelReason, value: "Flow.CancelReason" },
    { label: t.expectingChanges, value: "Flow.ExpectingFinancialChanges" },
    { label: t.expectingChangesNote, value: "Flow.ExpectingFinancialChangesNote" },
  ];

  // Document fields (always available)
  const documentFields = [{ label: t.today, value: "today" }];

  // Goal fields (only available in per_goal scope)
  const goalFields = [
    { label: t.goalName, value: "Goal Name" },
    { label: t.goalType, value: "Goal Type" },
    { label: t.goalDescription, value: "Goal Description" },
    { label: t.riskScore, value: "Risk Score" },
    // QL-375 group 10 — the goal's mandate (empty on a goal without one)
    { label: t.mandateName, value: "Mandate Name" },
    { label: t.mandateStatus, value: "Mandate Status" },
    { label: t.mandateSource, value: "Mandate Source" },
    { label: t.mandateOrigin, value: "Mandate Origin" },
    { label: t.mandateMode, value: "Mandate Mode" },
    { label: t.existingMandateName, value: "Existing Mandate Name" },
    { label: t.mandateDeviationNote, value: "Mandate Deviation Note" },
    { label: t.mandateDecidedBy, value: "Mandate Decided By" },
    { label: t.mandateDecidedAt, value: "Mandate Decided At" },
  ];
  // Per-portfolio sections only: QL-375 group 10b mandate verdict + QL-72 model deviation
  const portfolioSectionFields = [
    { label: t.portfolioMandateStatus, value: "Portfolio Mandate Status" },
    { label: t.portfolioMandateDeviationNote, value: "Portfolio Mandate Deviation Note" },
    { label: t.portfolioMandateDecidedBy, value: "Portfolio Mandate Decided By" },
    { label: t.portfolioMandateDecidedAt, value: "Portfolio Mandate Decided At" },
    // QL-72 — model portfolio picked from another risk score
    { label: t.portfolioModelDeviation, value: "Portfolio Model Deviation" },
    { label: t.portfolioModelRiskScore, value: "Portfolio Model Risk Score" },
    { label: t.portfolioGoalRiskScore, value: "Portfolio Goal Risk Score" },
  ];
  // QL-414 — the portfolio's own name, where a section prints per portfolio.
  if (sectionScope === "per_portfolio") {
    goalFields.push({ label: t.portfolioName, value: "Portfolio Name" });
  }

  // QP-150: per_portfolio sections also carry the parent goal's context, so goal fields apply.
  const isPerGoalScope = sectionScope === "per_goal" || sectionScope === "per_portfolio";

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label htmlFor="text-content">
          {t.textContent} <span className="text-destructive">*</span>
        </Label>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              {t.insertField}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 max-h-[400px] overflow-y-auto">
            <DropdownMenuLabel>{t.clientFields}</DropdownMenuLabel>
            <DropdownMenuGroup>
              {clientFields.map((field) => (
                <DropdownMenuItem key={field.value} onClick={() => insertField(field.value)}>
                  {field.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>

            {(() => {
              const customOnly = clientCustomFields.filter((f) => f.isActive && !f.isStandard);
              return customOnly.length > 0 ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>{t.customFields}</DropdownMenuLabel>
                  <DropdownMenuGroup>
                    {customOnly.map((field) => {
                      // Display localized name in the dropdown
                      // Insert display name - it will be converted to ID on save
                      const displayName = localize(field.nameI18n) || field.name;
                      return (
                        <DropdownMenuItem
                          key={field.id}
                          onClick={() => insertField(`Client.${displayName}`)}
                        >
                          {displayName}
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuGroup>
                </>
              ) : null;
            })()}

            <DropdownMenuSeparator />
            <DropdownMenuLabel>{t.ownerFields}</DropdownMenuLabel>
            <DropdownMenuGroup>
              {ownerFields.map((field) => (
                <DropdownMenuItem key={field.value} onClick={() => insertField(field.value)}>
                  {field.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>

            {isPerGoalScope && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>{t.goalFields}</DropdownMenuLabel>
                <DropdownMenuGroup>
                  {goalFields.map((field) => (
                    <DropdownMenuItem key={field.value} onClick={() => insertField(field.value)}>
                      {field.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
              </>
            )}

            {sectionScope === "per_portfolio" && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>{t.portfolioFields}</DropdownMenuLabel>
                <DropdownMenuGroup>
                  {portfolioSectionFields.map((field) => (
                    <DropdownMenuItem key={field.value} onClick={() => insertField(field.value)}>
                      {field.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
              </>
            )}

            {(() => {
              // Filter flow fields by scope:
              // - In global sections: only show global-scoped fields
              // - In per_goal sections: show all fields
              const filteredFlowFields = flowFields.filter((field) => {
                if (field.scope === "portfolio" && sectionScope !== "per_portfolio") return false;
                if (field.scope === "goal" && sectionScope === "global") return false;
                return true;
              });

              return filteredFlowFields.length > 0 ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>{t.flowFields}</DropdownMenuLabel>
                  <DropdownMenuGroup>
                    {filteredFlowFields.map((field) => {
                      // Insert display name - it will be converted to ID on save
                      // (by id when another field shares the name, QL-610)
                      const displayName = field.name;
                      return (
                        <DropdownMenuItem
                          key={field.id}
                          onClick={() => insertField(flowFieldDisplayRef(field, fieldMap))}
                        >
                          {displayName}
                          {field.scope === "goal" && (
                            <span className="ml-2 text-xs text-muted-foreground">[{t.goal}]</span>
                          )}
                          {field.scope === "portfolio" && (
                            <span className="ml-2 text-xs text-muted-foreground">
                              [{t.portfolio}]
                            </span>
                          )}
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuGroup>
                </>
              ) : null;
            })()}
            <DropdownMenuSeparator />
            <DropdownMenuLabel>{t.flow}</DropdownMenuLabel>
            <DropdownMenuGroup>
              {flowInstanceFields.map((field) => (
                <DropdownMenuItem key={field.value} onClick={() => insertField(field.value)}>
                  {field.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>
            <AdvisorNoteMenuItems
              pages={advisorNotePages}
              sectionScope={sectionScope}
              onInsert={(_label, field) => insertField(field)}
            />
            {activeQuizzes.length > 0 && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>{t.quizFields}</DropdownMenuLabel>
                <DropdownMenuGroup>
                  {activeQuizzes.map((quiz) => (
                    <DropdownMenuItem
                      key={quiz.id}
                      onClick={() => insertField(`Quiz.${quiz.name}`)}
                    >
                      {quiz.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
                <DropdownMenuLabel>{t.quizOverrideFields}</DropdownMenuLabel>
                <DropdownMenuGroup>
                  {activeQuizzes.map((quiz) => (
                    <DropdownMenuItem
                      key={quiz.id}
                      onClick={() => insertField(`Quiz.${quiz.name}.Override`)}
                    >
                      {quiz.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
                <DropdownMenuLabel>{t.quizExternalFields}</DropdownMenuLabel>
                <DropdownMenuGroup>
                  {activeQuizzes.map((quiz) => (
                    <DropdownMenuItem
                      key={quiz.id}
                      onClick={() => insertField(`Quiz.${quiz.name}.External`)}
                    >
                      {quiz.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuLabel>{t.document}</DropdownMenuLabel>
            <DropdownMenuGroup>
              {documentFields.map((field) => (
                <DropdownMenuItem key={field.value} onClick={() => insertField(field.value)}>
                  {field.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <Textarea
        ref={textareaRef}
        id="text-content"
        value={displayContent}
        onChange={(e) => handleContentChange(e.target.value)}
        placeholder={t.placeholder}
        rows={5}
        required
      />
      <p className="text-xs text-muted-foreground">{t.formattingHelp}</p>

      {/* Alignment and Font Size */}
      <div className="flex gap-4 pt-2">
        <div className="space-y-2">
          <Label htmlFor="text-align" className="text-xs text-muted-foreground">
            {t.alignment}
          </Label>
          <Select
            value={config.align || "left"}
            onValueChange={(value) =>
              onChange({ ...config, align: value as "left" | "center" | "right" })
            }
          >
            <SelectTrigger id="text-align" className="w-28">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="left">{t.left}</SelectItem>
              <SelectItem value="center">{t.center}</SelectItem>
              <SelectItem value="right">{t.right}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="text-font-size" className="text-xs text-muted-foreground">
            {t.fontSize}
          </Label>
          <Input
            id="text-font-size"
            type="number"
            min={6}
            max={72}
            value={config.fontSize || ""}
            onChange={(e) => {
              const value = e.target.value ? parseInt(e.target.value, 10) : undefined;
              onChange({ ...config, fontSize: value });
            }}
            placeholder={t.default}
            className="w-24"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="text-max-width" className="text-xs text-muted-foreground">
            {t.maxWidth}
          </Label>
          <Input
            id="text-max-width"
            type="number"
            min={1}
            max={20}
            step={0.5}
            value={config.maxWidth || ""}
            onChange={(e) => {
              const value = e.target.value ? parseFloat(e.target.value) : undefined;
              onChange({ ...config, maxWidth: value });
            }}
            placeholder={t.full}
            className="w-24"
          />
        </div>

        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">{t.textColor}</Label>
          <div>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="h-8 w-28 justify-start gap-2 px-2 font-mono text-xs"
                >
                  <span
                    className="h-5 w-5 rounded border border-input"
                    style={{
                      backgroundColor: config.color || "transparent",
                      backgroundImage: config.color
                        ? undefined
                        : "linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%), linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%)",
                      backgroundSize: config.color ? undefined : "8px 8px",
                      backgroundPosition: config.color ? undefined : "0 0, 4px 4px",
                    }}
                  />
                  <span className="truncate">{config.color || t.default}</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-3" align="start">
                <HexColorPicker
                  color={config.color || "#000000"}
                  onChange={(color) => onChange({ ...config, color })}
                />
                <Input
                  type="text"
                  value={config.color || ""}
                  onChange={(e) => onChange({ ...config, color: e.target.value || undefined })}
                  placeholder="#000000"
                  className="mt-2 w-full font-mono text-sm"
                />
                {config.color && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="mt-2 w-full"
                    onClick={() => onChange({ ...config, color: undefined })}
                  >
                    {t.reset}
                  </Button>
                )}
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>
    </div>
  );
}

export function validateTextConfig(config: TextConfig, lang = "en_GB"): string | null {
  const t = pickT(T, lang);
  if (!config.content?.trim()) {
    return t.validationError;
  }
  return null;
}

export function getDefaultTextConfig(): TextConfig {
  return {
    content: "",
  };
}
